import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatTab, MatTabGroup } from '@angular/material/tabs'
import { MatToolbar } from '@angular/material/toolbar'
import { ActivatedRoute } from '@angular/router'
import {
  CollectionUtil,
  ComparatorBuilder,
  ElderBasicPaneLayoutComponent,
  ElderDataToolbarComponent,
  ElderHeaderComponent,
  ElderPaneActionsComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneSubtitleComponent,
  ElderPaneTitleComponent,
  ElderSelectComponent,
  ElderSelectValueDirective,
  ElderStopEventPropagationDirective,
  ElderTabGroupRoutingDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderThemeDirective,
  ElderToastService,
  ElderToolbarContentDirective,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { WorkAction } from '../../../../shopfloor/workcenters/capability/capabilities/work-action'
import { Action } from '../../../actions/action'
import { ActionSelectDirective } from '../../../actions/action-select/action-select.directive'
import { CapabilitySpecService } from '../../capability-spec.service'
import { SkillSetSpec } from '../../skillsets/skill-set-spec'
import { SkillSetSpecDetailComponent } from '../../skillsets/skill-set-spec-detail/skill-set-spec-detail.component'
import { CapabilitySpec } from '../capability-spec'

@Component({
  selector: 'app-capability-spec-detail',
  templateUrl: './capability-spec-detail.component.html',
  styleUrls: ['./capability-spec-detail.component.scss'],
  imports: [
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneHeaderComponent,
    ElderPaneActionsComponent,
    ElderPaneContentComponent,
    ElderPaneTitleComponent,
    ElderPaneSubtitleComponent,
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    MatTabGroup,
    ElderTabGroupRoutingDirective,
    MatTab,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    ElderTableComponent,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    ElderSelectComponent,
    ActionSelectDirective,
    ElderSelectValueDirective,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    ElderStopEventPropagationDirective,
    SkillSetSpecDetailComponent,
    ElderHeaderComponent,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class CapabilitySpecDetailComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly _capabilitySpec$: BehaviorSubject<CapabilitySpec> = new BehaviorSubject(null)
  private readonly _actionSequence$: BehaviorSubject<WorkAction[]> = new BehaviorSubject([])
  private readonly _skillSetSpecs$: BehaviorSubject<SkillSetSpec[]> = new BehaviorSubject([])

  public readonly selectedSkillSet$ = new BehaviorSubject<SkillSetSpec>(null)

  private _creationMode: boolean

  @Input()
  public embedded = false

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  @ViewChild(ActionSelectDirective)
  public actionSelectDirective: ActionSelectDirective

  private specService = inject(CapabilitySpecService)
  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const route = inject(ActivatedRoute)
    this.capabilitySpec = route.snapshot.data['capability']
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    if (!this.capabilitySpec) {
      this.capabilitySpec = this.initNew()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get capabilitySpec$(): Observable<CapabilitySpec> {
    return this._capabilitySpec$.asObservable()
  }

  public get capabilitySpec(): CapabilitySpec {
    return this._capabilitySpec$.getValue()
  }

  @Input()
  public set capabilitySpec(spec: CapabilitySpec) {
    if (spec) {
      this._creationMode = !spec.id
      this._capabilitySpec$.next(spec)
      this.skillSetSpecs = spec.skillSets
      this.actionSequence = spec.actionSequence
      this.autoSelectSkillSetSpec()
    }
  }

  public get actionSequence$(): Observable<WorkAction[]> {
    return this._actionSequence$.asObservable()
  }

  public get actionSequence(): WorkAction[] {
    return this._actionSequence$.getValue()
  }

  public set actionSequence(actionSequence: WorkAction[]) {
    this._actionSequence$.next(actionSequence)
  }

  public get skillSetSpecs$(): Observable<SkillSetSpec[]> {
    return this._skillSetSpecs$.asObservable()
  }

  public get skillSetSpecs(): SkillSetSpec[] {
    return this._skillSetSpecs$.getValue()
  }

  public set skillSetSpecs(skillSetSpecs: SkillSetSpec[]) {
    skillSetSpecs.sort(ComparatorBuilder.fieldSort('id'))
    this._skillSetSpecs$.next(skillSetSpecs)
  }

  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.save()
  }

  public onDelete(event: Event): void {
    this.delete()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  public onNewSkillSetSpec(event: Event): void {
    const skillSets = [...this.skillSetSpecs]
    const spec = new SkillSetSpec()
    skillSets.push(spec)
    this.skillSetSpecs = skillSets
    this.selectedSkillSet$.next(spec)
  }

  public onRemoveSkillSetSpecs(toRemove: SkillSetSpec[]) {
    const skillSets = [...this.skillSetSpecs]
    toRemove.forEach((ss) => skillSets.splice(skillSets.indexOf(ss), 1))
    this.skillSetSpecs = skillSets
    this.selectedSkillSet$.next(null)
  }

  public onSkillSetClick(skillSet: SkillSetSpec): void {
    this.selectedSkillSet$.next(skillSet)
  }

  public onAddAction(action: Action): void {
    if (action) {
      const actions = [...this.actionSequence]

      const op = <WorkAction>{
        action: action.id,
      }
      actions.push(op)

      this.actionSequence = actions
      this.actionSelectDirective.clear()
    }
  }

  public onRemoveActions(toRemove: WorkAction[]) {
    const actionSequence = [...this.actionSequence]
    toRemove.forEach((wa) => actionSequence.splice(actionSequence.indexOf(wa), 1))
    this.actionSequence = actionSequence
  }

  public moveUp(action: WorkAction): void {
    const specs = [...this.actionSequence]
    CollectionUtil.moveUp(specs, action)
    this.actionSequence = specs
  }

  public moveDown(action: WorkAction): void {
    const specs = [...this.actionSequence]
    CollectionUtil.moveDown(specs, action)
    this.actionSequence = specs
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private save(): void {
    if (this.creationMode && this.capabilitySpec.id) {
      this.specService.findById(this.capabilitySpec.id).subscribe({
        next: (_) => this.toastService.info('ID already exists!'),
        error: (_) => this.saveToDB(), // Id does not exist yet -> save
      })
    } else {
      this.saveToDB()
    }
  }

  private saveToDB(): void {
    this.capabilitySpec.skillSets = this.skillSetSpecs
    this.capabilitySpec.actionSequence = this.actionSequence

    this.specService.save(this.capabilitySpec).subscribe({
      next: (updated) => {
        this.capabilitySpec = updated
        this.autoSelectSkillSetSpec()
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.persisting_failed', err),
    })
  }

  private delete(): void {
    this.specService.delete(this.capabilitySpec).subscribe({
      next: (_) => {
        this.emitClose()
        this.toastService.info('messages.deleting_successful')
      },
      error: (err) => this.toastService.error('messages.deleting_failed', err),
    })
  }

  private autoSelectSkillSetSpec(): void {
    let toSelect = null
    if (this.selectedSkillSet$.getValue()) {
      toSelect = this.capabilitySpec.skillSets.find(
        (spec) => spec.id === this.selectedSkillSet$.getValue().id
      )
    } else if (this.capabilitySpec.skillSets.length > 0) {
      toSelect = this.capabilitySpec.skillSets[0]
    }

    this.selectedSkillSet$.next(toSelect)
  }

  private emitClose(event?: Event): void {
    this.close.emit(event)
  }

  private initNew(): CapabilitySpec {
    return new CapabilitySpec()
  }
}
